import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
} from 'react';
import {
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  IconButton,
  Box,
  Typography,
  Collapse,
  Tooltip,
  Checkbox,
  Button,
  TextField,
  InputAdornment,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  Receipt,
  ArrowUpward,
  ArrowDownward,
  OpenInFull,
  CloseFullscreen,
  Close,
  Description,
  AccountBalance,
  Visibility,
  FilterList,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import editIconImage from '../../../assests/images/Icons/editIcon.svg';
import cardMatchingIcon from '../../../assests/images/Icons/cardMatching.svg';
import { beautifyNumber } from '../../../Utils/FormatNumber';
import SummaryRowLabel, { SummaryChildLabel, summaryChildRowSx } from '../../Widgets/SummaryRowLabel/SummaryRowLabel';
import Constants from '../../../assests/Constants/constants.js';
import '../../../pages/Document/Document.scss';

/** Match PaymentAcceptanceTable scrollbar look for TableContainer overflow. */
const TABLE_SCROLLBAR_SX = {
  scrollbarWidth: 'var(--register-scrollbar-width, var(--xtram-scrollbar-width, thin))',
  scrollbarColor: '#d8daf2 transparent',
  '&::-webkit-scrollbar': {
    width: '6px',
    height: '6px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#d8daf2',
    borderRadius: 'var(--xtram-radius-pill, 999px)',
  },
};

// Resolve a numeric foreign-currency code to a display label like 'מט"ח ($)'.
const buildFcLabel = (currencyCode) => {
  if (currencyCode === null || currencyCode === undefined) return 'מט"ח';
  const name = Object.entries(Constants.ForeignCurrencyCode || {})
    .find(([, value]) => value === currencyCode)?.[0];
  const symbol = name ? (Constants.ForeignCurrencySymbol?.[name] || name) : '';
  return symbol ? `מט"ח (${symbol})` : 'מט"ח';
};

// Columns for parent table (Account Cards)
const PARENT_COLS = (t) => [
  { id: 'palCode', label: t('PalCode'), type: 'string', width: '80px' },
  { id: 'accountCardId', label: t('CardId'), type: 'string', width: '80px' },
  { id: 'accountCardName', label: t('CardName'), type: 'string', width: '120px' },
  { id: 'initialDebit', label: t('InitialDebit'), type: 'number', width: '100px' },
  { id: 'initialCredit', label: t('InitialCredit'), type: 'number', width: '100px' },
  { id: 'totalDebit', label: t('Debit'), type: 'number', width: '100px' },
  { id: 'totalCredit', label: t('Credit'), type: 'number', width: '100px' },
  { id: 'balance', label: t('Balance'), type: 'number', width: '100px' },
];

// Child table columns for MUI Table (opening balance + rows + totals always visible in one scroll)
const CHILD_TABLE_COLS = (t, showFc, fcLabel, fcOnly) => [
  { id: 'edit', label: '', type: 'action', width: 140 },
  { id: 'creditAccountName', label: t('Credit Account'), type: 'string', width: 120 },
  { id: 'debitAccountName', label: t('Debit Account'), type: 'string', width: 120 },
  ...(fcOnly
    ? [
        { id: 'foreignCurrencyDebit', label: `${t('Debit')} ${fcLabel}`, type: 'number', width: 90 },
        { id: 'foreignCurrencyCredit', label: `${t('Credit')} ${fcLabel}`, type: 'number', width: 90 },
        { id: 'foreignCurrencyBalance', label: `${t('Balance')} ${fcLabel}`, type: 'number', width: 90 },
      ]
    : [
        { id: 'debitAccount', label: t('Debit'), type: 'number', width: 90 },
        { id: 'creditAccount', label: t('Credit'), type: 'number', width: 90 },
        { id: 'balance', label: t('Balance'), type: 'number', width: 90 },
        ...(showFc
          ? [
              { id: 'foreignCurrencyDebit', label: `${t('Debit')} ${fcLabel}`, type: 'number', width: 90 },
              { id: 'foreignCurrencyCredit', label: `${t('Credit')} ${fcLabel}`, type: 'number', width: 90 },
              { id: 'foreignCurrencyBalance', label: `${t('Balance')} ${fcLabel}`, type: 'number', width: 90 },
            ]
          : []),
      ]),
  { id: 'uploadDate', label: t('RegistrationDate'), type: 'date', width: 100 },
  { id: 'documentDate', label: t('DocumentDate'), type: 'date', width: 100 },
  { id: 'reference1', label: t('Reference1'), type: 'string', width: 100 },
  { id: 'reference2', label: t('Reference2'), type: 'string', width: 100 },
  { id: 'description', label: t('Details'), type: 'string', width: 150 },
];

const formatCellValue = (value, type, formatDateNew, t) => {
  if (value == null || value === '') return '-';
  switch (type) {
    case 'number':
      return beautifyNumber(value);
    case 'date':
      return formatDateNew(value);
    default:
      return typeof value === 'string' && t ? t(value, { defaultValue: value }) : value;
  }
};

/* Same logic as DocumentDataGrid – simple width 100%, no overflow/minWidth on root */
const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
  width: '100%',
  '& .MuiAutocomplete-root': {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  '& *': {
    boxSizing: 'border-box !important',
  },
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: '#E3E7FE',
  },
  '& .MuiDataGrid-columnHeader': {
    backgroundColor: '#E3E7FE',
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    color: '#1222b3',
    fontFamily: 'Montserrat',
    fontSize: '14px',
    fontWeight: 600,
    lineHeight: 'normal',
  },
  '& .MuiDataGrid-cell': {
    borderTop: '1px solid rgba(191, 191, 193, 0.56)',
    backgroundColor: 'rgba(234, 237, 255, 0.32)',
    whiteSpace: 'normal',
    wordWrap: 'break-word',
  },
  '& .MuiDataGrid-row:hover': {
    backgroundColor: 'rgba(191, 200, 255, 0.3)',
  },
  '& .MuiDataGrid-cell:focus': {
    outline: 'none',
  },
  '& .MuiDataGrid-cell': {
    display: 'flex',
    alignItems: 'center',
  },
  /* Slim scrollbars so they don't take half the screen */
  '& .MuiDataGrid-virtualScroller': {
    overflowX: 'auto',
    overflowY: 'auto',
    scrollbarWidth: 'var(--register-scrollbar-width, var(--xtram-scrollbar-width, thin))',
    scrollbarColor: '#d8daf2 transparent',
    borderRadius: 'inherit',
  },
  '& .MuiDataGrid-virtualScroller::-webkit-scrollbar': {
    width: '6px',
    height: '6px',
  },
  '& .MuiDataGrid-virtualScroller::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb': {
    background: '#d8daf2',
    borderRadius: 'var(--xtram-radius-pill, 999px)',
  },
  '& .MuiDataGrid-scrollbar': {
    scrollbarWidth: 'var(--register-scrollbar-width, var(--xtram-scrollbar-width, thin))',
    scrollbarColor: '#d8daf2 transparent',
    borderRadius: 'inherit',
  },
  '& .MuiDataGrid-scrollbar::-webkit-scrollbar': {
    width: '6px',
    height: '6px',
  },
  '& .MuiDataGrid-scrollbar::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '& .MuiDataGrid-scrollbar::-webkit-scrollbar-thumb': {
    background: '#d8daf2',
    borderRadius: 'var(--xtram-radius-pill, 999px)',
  },
  /* Compact pagination – less vertical space */
  '& .MuiDataGrid-footerContainer': {
    minHeight: 40,
    borderTop: '1px solid rgba(224, 224, 224, 1)',
  },
  '& .MuiTablePagination-root': {
    minHeight: 40,
  },
  '& .MuiTablePagination-toolbar': {
    minHeight: 40,
    paddingLeft: 8,
    paddingRight: 8,
  },
  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
    fontSize: 12,
    margin: 0,
  },
}));

const AccountCardCollapsibleTable = ({
  accountCards = [],
  handleEdit,
  handleSortChange,
  formatDateNew,
  showZeros = true,
  sortConfig = { key: '', direction: 'ascending' },
  onSelectedCardsChange,
  onCardMatchingClick,
  onGenerateLedgerCard,
  searchQuery = '',
  selectedCurrency = null,
}) => {
  const { t, i18n } = useTranslation();
  const direction = i18n.dir();

  const showFc = selectedCurrency !== null && selectedCurrency !== undefined;
  const fcOnly = typeof selectedCurrency === 'number';
  const fcLabel = useMemo(() => buildFcLabel(selectedCurrency), [selectedCurrency]);

  /* Selected card state - which card's transactions to show */
  const [selectedCardId, setSelectedCardId] = useState(null);
  const [isTransactionsExpanded, setIsTransactionsExpanded] = useState(true);
  const [isTransactionsFullWidth, setIsTransactionsFullWidth] = useState(false);

  /* Local sort for child table only - avoids parent sort affecting child and duplicate rows */
  const [childSortModel, setChildSortModel] = useState([{ field: 'documentDate', sort: 'asc' }]);
  /* Local search for child table (transactions) */
  const [childSearchQuery, setChildSearchQuery] = useState('');
  /* Selected transaction row in child grid – used for toolbar and handleEdit */
  const [selectedTransactionId, setSelectedTransactionId] = useState(null);

  /* Selected cards for batch operations (checkboxes) */
  const [selectedCards, setSelectedCards] = useState(new Set());

  /* Pagination for parent table */
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  useEffect(() => setPage(0), [accountCards, searchQuery]);
  useEffect(() => setSelectedTransactionId(null), [selectedCardId]);

  /* Pagination for child table (transactions) */
  const [transactionsPage, setTransactionsPage] = useState(0);
  const [transactionsRowsPerPage, setTransactionsRowsPerPage] = useState(25);
  useEffect(() => setTransactionsPage(0), [selectedCardId, childSearchQuery]);

  /* Column filters for child table */
  const [columnFilters, setColumnFilters] = useState({});
  const [showColumnFilters, setShowColumnFilters] = useState(false);
  const activeFilterCount = useMemo(
    () => Object.values(columnFilters).filter((v) => v != null && String(v).trim() !== '').length,
    [columnFilters]
  );
  const handleColumnFilterChange = useCallback((fieldId, value) => {
    setColumnFilters((prev) => ({ ...prev, [fieldId]: value }));
    setTransactionsPage(0);
  }, []);
  const clearColumnFilters = useCallback(() => {
    setColumnFilters({});
    setTransactionsPage(0);
  }, []);

  /* Columns */
  const parentCols = useMemo(() => PARENT_COLS(t), [t]);

  /* Calculate initial debit/credit from opening balance (fallback when API amounts are missing) */
  const calculateInitials = useCallback((card) => {
    // Prefer API opening amounts (debitAccountAmount / creditAccountAmount / balance).
    // These are what AccountCardReportHTML uses and are already the correct opening.
    if (card.debitAccountAmount !== undefined || card.creditAccountAmount !== undefined) {
      const initialDebit = card.debitAccountAmount ?? 0;
      const initialCredit = card.creditAccountAmount ?? 0;
      return {
        initialDebit,
        initialCredit,
        openingBalance: initialDebit - initialCredit,
      };
    }

    if (card.balance !== undefined && card.balance !== null) {
      const openingBalance = card.balance;
      return {
        initialDebit: openingBalance >= 0 ? openingBalance : 0,
        initialCredit: openingBalance < 0 ? Math.abs(openingBalance) : 0,
        openingBalance,
      };
    }

    // Last resort: reverse-engineer from running balances.
    // Use ?? (not ||) so a final balance of 0 is not treated as missing
    // and replaced with card.balance (which would double the opening).
    const transactions = card.transactions || [];
    const transactionDebitSum = transactions.reduce((sum, txn) => sum + (txn.debitAccount || 0), 0);
    const transactionCreditSum = transactions.reduce((sum, txn) => sum + (txn.creditAccount || 0), 0);

    const finalBalance = transactions.length > 0
      ? (transactions[transactions.length - 1].balance ?? 0)
      : 0;

    const openingBalance = finalBalance - transactionDebitSum + transactionCreditSum;

    return {
      initialDebit: openingBalance >= 0 ? openingBalance : 0,
      initialCredit: openingBalance < 0 ? Math.abs(openingBalance) : 0,
      openingBalance,
    };
  }, []);

  /* Process account cards with calculated values */
  const processedCards = useMemo(() => {
    return accountCards.map((card) => {
      // Prefer explicit initials, otherwise take API opening / calculated initials
      const initials = calculateInitials(card);
      const initialDebit = card.initialDebit !== undefined ? card.initialDebit : initials.initialDebit;
      const initialCredit = card.initialCredit !== undefined ? card.initialCredit : initials.initialCredit;
      const openingBalance = initialDebit - initialCredit;

    // Calculate totals by summing all transactions (NOT using card.debitAccountAmount/creditAccountAmount)
    const transactions = card.transactions || [];
    const transactionDebitSum = transactions.reduce((sum, txn) => sum + (txn.debitAccount || 0), 0);
    const transactionCreditSum = transactions.reduce((sum, txn) => sum + (txn.creditAccount || 0), 0);

    // Total debit = sum of all transaction debits ONLY (not including opening balance)
    const totalDebit = transactionDebitSum;
    // Total credit = sum of all transaction credits ONLY (not including opening balance)
    const totalCredit = transactionCreditSum;
    // Balance = (transaction debits + opening debit) - (transaction credits + opening credit)
    const balance = (transactionDebitSum + initialDebit) - (transactionCreditSum + initialCredit);

      return {
        ...card,
        initialDebit,
        initialCredit,
        openingBalance,
        totalDebit,
        totalCredit,
        balance,
      };
    });
  }, [accountCards, calculateInitials]);


  /* Filter cards based on showZeros setting */
  /* Filter cards based on showZeros setting and searchQuery */
  const filteredCards = useMemo(() => {
    let filtered = processedCards;

    // Hiding zeros is about the bottom line: a card that nets out to zero is settled and
    // drops out, even when money moved through it during the period.
    if (!showZeros) {
      filtered = filtered.filter((card) => Math.abs(Number(card.balance) || 0) >= 0.005);
    }
    
    // Then filter by searchQuery if provided - search in ALL fields
    if (searchQuery && searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(card => {
        // Search in card-level fields - ALL fields
        const matchesCard = 
          card?.palCode?.toLowerCase().includes(query) ||
          card?.accountCardId?.toString().toLowerCase().includes(query) ||
          card?.accountCardName?.toLowerCase().includes(query) ||
          card?.initialDebit?.toString().includes(query) ||
          card?.initialCredit?.toString().includes(query) ||
          card?.totalDebit?.toString().includes(query) ||
          card?.totalCredit?.toString().includes(query) ||
          card?.balance?.toString().includes(query) ||
          card?.openingBalance?.toString().includes(query) ||
          card?.debitAccountAmount?.toString().includes(query) ||
          card?.creditAccountAmount?.toString().includes(query) ||
          card?.description?.toLowerCase().includes(query) ||
          card?.palCodeCategory?.toLowerCase().includes(query) ||
          card?.palCodeAccountGroup?.toLowerCase().includes(query) ||
          card?.palCodeType?.toLowerCase().includes(query);

        // Also check if any transaction in this card matches - ALL transaction fields
        const hasMatchingTransaction = card.transactions && card.transactions.some(transaction => {
          return (
            transaction?.description?.toLowerCase().includes(query) ||
            transaction?.reference1?.toLowerCase().includes(query) ||
            transaction?.reference2?.toLowerCase().includes(query) ||
            transaction?.debitAccount?.toString().includes(query) ||
            transaction?.creditAccount?.toString().includes(query) ||
            transaction?.balance?.toString().includes(query) ||
            transaction?.debitAccountAmount?.toString().includes(query) ||
            transaction?.creditAccountAmount?.toString().includes(query) ||
            new Date(transaction.documentDate).toLocaleDateString().toLowerCase().includes(query) ||
            new Date(transaction.uploadDate).toLocaleDateString().toLowerCase().includes(query) ||
            transaction?.cardId?.toString().toLowerCase().includes(query) ||
            transaction?.cardName?.toLowerCase().includes(query) ||
            transaction?.palCode?.toLowerCase().includes(query) ||
            transaction?.creditAccountName?.toLowerCase().includes(query) ||
            transaction?.debitAccountName?.toLowerCase().includes(query) ||
            transaction?.type1?.toLowerCase().includes(query) ||
            transaction?.docType?.toLowerCase().includes(query) ||
            transaction?.id?.toString().includes(query) ||
            transaction?.ID?.toString().includes(query) ||
            transaction?.confirmationNumber?.toLowerCase().includes(query) ||
            transaction?.accountCodeName?.toLowerCase().includes(query) ||
            transaction?.accountCaseId?.toLowerCase().includes(query) ||
            transaction?.sortCodeId?.toString().includes(query) ||
            transaction?.sortCodeName?.toLowerCase().includes(query) ||
            transaction?.vat?.toString().includes(query) ||
            transaction?.amount?.toString().includes(query)
          );
        });

        return matchesCard || hasMatchingTransaction;
      });
    }

    return filtered;
  }, [processedCards, showZeros, searchQuery]);

  /* Sort filtered cards */
  const sortedCards = useMemo(() => {
    if (!sortConfig.key) return filteredCards;

    const sorted = [...filteredCards];
    sorted.sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];

      if (sortConfig.type === 'number') {
        return sortConfig.direction === 'ascending'
          ? (aVal || 0) - (bVal || 0)
          : (bVal || 0) - (aVal || 0);
      } else {
        const aStr = String(aVal || '').toLowerCase();
        const bStr = String(bVal || '').toLowerCase();
        return sortConfig.direction === 'ascending'
          ? aStr.localeCompare(bStr)
          : bStr.localeCompare(aStr);
      }
    });

    return sorted;
  }, [filteredCards, sortConfig]);

  /* Get selected card */
  const selectedCard = useMemo(() => {
    return processedCards.find(card => card.accountCardId === selectedCardId);
  }, [processedCards, selectedCardId]);

  /* Calculate first row (opening balance) for selected card */
  const firstRowForCard = useMemo(() => {
    if (!selectedCard) return null;

    const fcBal = selectedCard.foreignCurrencyInitialBalance ?? 0;
    if (fcOnly) {
      if (fcBal === 0) return null;
      return {
        foreignCurrencyDebit: fcBal > 0 ? fcBal : 0,
        foreignCurrencyCredit: fcBal < 0 ? Math.abs(fcBal) : 0,
        foreignCurrencyBalance: fcBal,
        description: selectedCard.description || t('OpeningBalance', { defaultValue: 'Opening Balance' }),
      };
    }

    const initialDebit = selectedCard.initialDebit || 0;
    const initialCredit = selectedCard.initialCredit || 0;
    const openingBalance = initialDebit - initialCredit;

    if (initialDebit === 0 && initialCredit === 0) {
      return null;
    }

    return {
      debitAccountAmount: initialDebit,
      creditAccountAmount: initialCredit,
      balance: openingBalance,
      description: selectedCard.description || t('OpeningBalance', { defaultValue: 'Opening Balance' }),
    };
  }, [selectedCard, fcOnly, t]);

  /* Filter transactions (no parent sort – child table uses DataGrid sort to avoid duplicates) */
  const filteredTransactions = useMemo(() => {
    if (!selectedCard || !selectedCard.transactions) return [];

    // Assign stable unique id from original index so sorting never produces duplicate keys
    const withStableId = selectedCard.transactions.map((txn, i) => ({
      ...txn,
      id: txn.id ?? `txn-${selectedCardId}-${i}`,
    }));

    let filtered = withStableId;

    // First filter by showZeros
    if (!showZeros) {
      filtered = filtered.filter(transaction => {
        if (fcOnly) {
          const hasFcDebit = (transaction.foreignCurrencyDebit || 0) !== 0;
          const hasFcCredit = (transaction.foreignCurrencyCredit || 0) !== 0;
          const hasFcBalance = (transaction.foreignCurrencyBalance || 0) !== 0;
          return hasFcDebit || hasFcCredit || hasFcBalance;
        }
        const hasNonZeroDebit = (transaction.debitAccount || 0) !== 0;
        const hasNonZeroCredit = (transaction.creditAccount || 0) !== 0;
        const hasNonZeroBalance = (transaction.balance || 0) !== 0;

        return hasNonZeroDebit || hasNonZeroCredit || hasNonZeroBalance;
      });
    }

    // Then filter by searchQuery if provided - search in ALL fields
    if (searchQuery && searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(transaction => {
        return (
          transaction?.description?.toLowerCase().includes(query) ||
          transaction?.reference1?.toLowerCase().includes(query) ||
          transaction?.reference2?.toLowerCase().includes(query) ||
          transaction?.debitAccount?.toString().includes(query) ||
          transaction?.creditAccount?.toString().includes(query) ||
          transaction?.balance?.toString().includes(query) ||
          transaction?.debitAccountAmount?.toString().includes(query) ||
          transaction?.creditAccountAmount?.toString().includes(query) ||
          new Date(transaction.documentDate).toLocaleDateString().toLowerCase().includes(query) ||
          new Date(transaction.uploadDate).toLocaleDateString().toLowerCase().includes(query) ||
          transaction?.cardId?.toString().toLowerCase().includes(query) ||
          transaction?.cardName?.toLowerCase().includes(query) ||
          transaction?.palCode?.toLowerCase().includes(query) ||
          transaction?.creditAccountName?.toLowerCase().includes(query) ||
          transaction?.debitAccountName?.toLowerCase().includes(query) ||
          transaction?.type1?.toLowerCase().includes(query) ||
          transaction?.docType?.toLowerCase().includes(query) ||
          transaction?.id?.toString().includes(query) ||
          transaction?.ID?.toString().includes(query) ||
          transaction?.confirmationNumber?.toLowerCase().includes(query) ||
          transaction?.accountCodeName?.toLowerCase().includes(query) ||
          transaction?.accountCaseId?.toLowerCase().includes(query) ||
          transaction?.sortCodeId?.toString().includes(query) ||
          transaction?.sortCodeName?.toLowerCase().includes(query) ||
          transaction?.vat?.toString().includes(query) ||
          transaction?.amount?.toString().includes(query)
        );
      });
    }

    return filtered;
  }, [selectedCard, selectedCardId, showZeros, searchQuery, fcOnly]);

  /* Foreign-currency opening balance for the selected card (single-currency scope) */
  const fcInitialBalance = useMemo(() => {
    if (!showFc || !selectedCard) return 0;
    return selectedCard.foreignCurrencyInitialBalance ?? 0;
  }, [showFc, selectedCard]);

  /* Calculate totals for transactions table including first row */
  const transactionTotals = useMemo(() => {
    const initialDebit = firstRowForCard?.debitAccountAmount || 0;
    const initialCredit = firstRowForCard?.creditAccountAmount || 0;

    const sum = (key) =>
      filteredTransactions.reduce(
        (acc, cur) => acc + (cur[key] || 0),
        0
      );

    // Totals SHOULD include opening balance (initial amounts + transactions)
    const debit = sum('debitAccount') + initialDebit;
    const credit = sum('creditAccount') + initialCredit;
    // Balance SHOULD include opening balance
    const balance = (sum('debitAccount') + initialDebit) - (sum('creditAccount') + initialCredit);

    // Foreign-currency totals (single-currency scope) including FC opening balance
    const fcInitialDebit = fcInitialBalance > 0 ? fcInitialBalance : 0;
    const fcInitialCredit = fcInitialBalance < 0 ? Math.abs(fcInitialBalance) : 0;
    const fcDebit = sum('foreignCurrencyDebit') + fcInitialDebit;
    const fcCredit = sum('foreignCurrencyCredit') + fcInitialCredit;
    const fcBalance = fcDebit - fcCredit;

    return {
      debit,
      credit,
      balance,
      fcDebit,
      fcCredit,
      fcBalance,
    };
  }, [filteredTransactions, firstRowForCard, fcInitialBalance]);

  /* Paginated parent rows */
  const paginatedCards = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedCards.slice(start, start + rowsPerPage);
  }, [sortedCards, page, rowsPerPage]);

  const handleChangePage = useCallback((_, p) => setPage(p), []);
  const handleChangeRowsPerPage = useCallback((e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  }, []);

  /* DataGrid columns for child table (transactions) */
  const childGridColumns = useMemo(() => {
    const formatDate = (val) => (val ? formatDateNew(val) : '-');
    return [
      {
        field: 'edit',
        headerName: '',
       flex:1,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEdit(params.row); }}>
              <img src={editIconImage} alt="edit" />
            </IconButton>
            <Typography variant="caption" sx={{ fontSize: '10px', color: '#304FFF' }}>
              {t('ViewTransaction', { defaultValue: 'View Transaction' })}
            </Typography>
          </Box>
        ),
      },
      { field: 'creditAccountName', headerName: t('Credit Account'), width: 120, type: 'string' },
      { field: 'debitAccountName', headerName: t('Debit Account'), width: 120, type: 'string' },
      {
        field: 'debitAccount',
        headerName: t('Debit'),
        flex:1,
        type: 'number',
        valueFormatter: (v) => (v != null && v !== '' ? beautifyNumber(v) : '-'),
      },
      {
        field: 'creditAccount',
        headerName: t('Credit'),
        flex:1,
        type: 'number',
        valueFormatter: (v) => (v != null && v !== '' ? beautifyNumber(v) : '-'),
      },
      {
        field: 'balance',
        headerName: t('Balance'),
        flex:1,
        type: 'number',
        valueFormatter: (v) => (v != null && v !== '' ? beautifyNumber(v) : '-'),
      },
      {
        field: 'uploadDate',
        headerName: t('ValueDate'),
        flex:1,
        type: 'date',
        valueFormatter: (v) => formatDate(v),
      },
      {
        field: 'documentDate',
        headerName: t('ReferenceDate'),
        flex:1,
        type: 'date',
        valueFormatter: (v) => formatDate(v),
      },
      { field: 'reference1', headerName: t('Reference1'), flex:1 },
      { field: 'reference2', headerName: t('Reference2'), flex:1 },
      {
        field: 'description',
        headerName: t('Details'),
        width: 150,
        valueFormatter: (v) => (v ? t(v, { defaultValue: v }) : '-'),
        renderCell: (params) => {
          const text = params.value ? t(params.value, { defaultValue: params.value }) : '-';
          if (!params.row?._summary) return text;
          return <SummaryRowLabel row={params.row} text={text} t={t} />;
        },
      },
    ];
  }, [t, formatDateNew, handleEdit]);

  /* Rows for child DataGrid: filter by local search (like JournalEntryDataGrid) */
  const childGridRows = useMemo(() => {
    if (!childSearchQuery || !childSearchQuery.trim()) return filteredTransactions;
    const q = childSearchQuery.toLowerCase().trim();
    return filteredTransactions.filter((row) => {
      const str = (v) => (v != null && v !== '' ? String(v).toLowerCase() : '');
      return (
        str(row.description).includes(q) ||
        str(row.reference1).includes(q) ||
        str(row.reference2).includes(q) ||
        str(row.creditAccountName).includes(q) ||
        str(row.debitAccountName).includes(q) ||
        str(row.debitAccount).includes(q) ||
        str(row.creditAccount).includes(q) ||
        str(row.balance).includes(q) ||
        str(row.uploadDate).includes(q) ||
        str(row.documentDate).includes(q)
      );
    });
  }, [filteredTransactions, childSearchQuery]);

  /* Child table: apply column filters then sort then paginate */
  const columnFilteredRows = useMemo(() => {
    const hasActive = Object.entries(columnFilters).some(([, v]) => v != null && String(v).trim() !== '');
    if (!hasActive) return childGridRows;
    return childGridRows.filter((row) => {
      return Object.entries(columnFilters).every(([fieldId, value]) => {
        const v = value != null ? String(value).trim() : '';
        if (v === '') return true;
        const cell = row[fieldId];
        const str = cell != null && cell !== '' ? String(cell).toLowerCase() : '';
        return str.includes(v.toLowerCase());
      });
    });
  }, [childGridRows, columnFilters]);

  const sortedChildRows = useMemo(() => {
    const sortItem = childSortModel?.[0];
    if (!sortItem || !sortItem.field) return columnFilteredRows;
    const sorted = [...columnFilteredRows].sort((a, b) => {
      const aVal = a[sortItem.field];
      const bVal = b[sortItem.field];
      const cmp = (av, bv) => {
        if (typeof av === 'number' && typeof bv === 'number') return av - bv;
        const aDate = av ? new Date(av).getTime() : 0;
        const bDate = bv ? new Date(bv).getTime() : 0;
        if (aDate || bDate) return aDate - bDate;
        return String(av ?? '').toLowerCase().localeCompare(String(bv ?? '').toLowerCase());
      };
      return sortItem.sort === 'asc' ? cmp(aVal, bVal) : cmp(bVal, aVal);
    });
    return sorted;
  }, [columnFilteredRows, childSortModel]);

  const paginatedChildRows = useMemo(() => {
    const start = transactionsPage * transactionsRowsPerPage;
    return sortedChildRows.slice(start, start + transactionsRowsPerPage);
  }, [sortedChildRows, transactionsPage, transactionsRowsPerPage]);

  /* Summary view: which merged rows are opened to show the transactions behind them. */
  const [expandedSummaryRows, setExpandedSummaryRows] = useState({});
  useEffect(() => setExpandedSummaryRows({}), [selectedCardId]);
  const toggleSummaryRow = useCallback((rowId) => {
    setExpandedSummaryRows((prev) => ({ ...prev, [rowId]: !prev[rowId] }));
  }, []);

  /* A merged row is followed by its own transactions while it is open. */
  const displayedChildRows = useMemo(
    () => paginatedChildRows.flatMap((row) => {
      if (!row._summary || !expandedSummaryRows[row.id]) return [row];
      const children = (row._children || []).map((child, index) => ({
        ...child,
        id: `${row.id}:child:${child.id ?? index}`,
        _summaryChild: true,
        _sourceRow: child,
      }));
      return [row, ...children];
    }),
    [paginatedChildRows, expandedSummaryRows]
  );

  const getChildSortIcon = useCallback(
    (fieldId) => {
      const item = childSortModel?.[0];
      if (!item || item.field !== fieldId) return null;
      return item.sort === 'asc' ? <ArrowUpward sx={{ fontSize: 14 }} /> : <ArrowDownward sx={{ fontSize: 14 }} />;
    },
    [childSortModel]
  );

  const childTableCols = useMemo(() => CHILD_TABLE_COLS(t, showFc, fcLabel, fcOnly), [t, showFc, fcLabel, fcOnly]);

  /* Header style */
  const headerStyle = {
    backgroundColor: '#E3E7FE',
    color: '#1222b3',
    textAlign: 'center',
    fontFamily: 'Montserrat',
    fontSize: '12px',
    fontWeight: 'bold',
    height: '40px',
    padding: '4px 8px',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  };

  const childHeaderStyle = {
    backgroundColor: '#E3E7FE',
    color: '#1222b3',
    textAlign: 'center',
    fontFamily: 'Montserrat',
    fontSize: '13px',
    fontWeight: 600,
    height: '35px',
    padding: '4px 6px',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  };

  /* Calculate totals */
  const totals = useMemo(() => {
    return filteredCards.reduce(
      (acc, card) => ({
        initialDebit: acc.initialDebit + (card.initialDebit || 0),
        initialCredit: acc.initialCredit + (card.initialCredit || 0),
        totalDebit: acc.totalDebit + (card.totalDebit || 0),
        totalCredit: acc.totalCredit + (card.totalCredit || 0),
        balance: acc.balance + (card.balance || 0),
      }),
      { initialDebit: 0, initialCredit: 0, totalDebit: 0, totalCredit: 0, balance: 0 }
    );
  }, [filteredCards]);

  const handleCardClick = useCallback((cardId, e) => {
    // Don't trigger if clicking on checkbox
    if (e && e.target.closest('.card-checkbox')) {
      return;
    }
    setSelectedCardId(prev => prev === cardId ? null : cardId);
    setIsTransactionsExpanded(true);
  }, []);

  const handleCardSelect = useCallback((cardId, checked) => {
    setSelectedCards(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(cardId);
      } else {
        newSet.delete(cardId);
      }

      // Notify parent component of selected cards
      if (onSelectedCardsChange) {
        const selectedCardsData = accountCards.filter(card => newSet.has(card.accountCardId));
        onSelectedCardsChange(selectedCardsData);
      }

      return newSet;
    });
  }, [accountCards, onSelectedCardsChange]);

  const handleCardMatchingClick = useCallback((card, e) => {
    e.stopPropagation();
    // Don't navigate, just call the handler if provided
    if (onCardMatchingClick) {
      onCardMatchingClick(card);
    }
  }, [onCardMatchingClick]);

  const handleGenerateLedgerCardClick = useCallback((card) => {
    if (onGenerateLedgerCard) {
      onGenerateLedgerCard(card);
    }
  }, [onGenerateLedgerCard]);

  const handleHeaderSort = useCallback((colId, colType) => {
    if (handleSortChange) {
      handleSortChange(colId, colType);
    }
  }, [handleSortChange]);

  const getSortIcon = (colId) => {
    if (sortConfig.key !== colId) return null;
    return sortConfig.direction === 'ascending' ? (
      <ArrowUpward sx={{ fontSize: '14px', ml: 0.5 }} />
    ) : (
      <ArrowDownward sx={{ fontSize: '14px', ml: 0.5 }} />
    );
  };

  return (
    <div dir={direction} style={{ width: '100%' }}>
      {/* Tables Container – no fixed height so page can scroll */}
      <Box
        sx={{
          display: 'flex',
          gap: 1.5,
          width: '100%',
          flexDirection: 'row',
          overflowX: 'auto',
          overflowY: 'visible',
          justifyContent: selectedCardId ? 'flex-start' : 'center',
        }}
      >
        {/* Parent Table - Account Cards */}
        {(!selectedCardId || !isTransactionsFullWidth) && (
          <Box
            sx={{
              minWidth: selectedCardId && !isTransactionsFullWidth ? '40%' : '100%',
              flex: selectedCardId && !isTransactionsFullWidth ? '0 0 40%' : '1 1 auto',
              display: 'flex',
              flexDirection: 'column',
              height: '50vh',
            }}
          >
            <TableContainer
              component={Paper}
              sx={{
                height: '100%',
                overflow: 'auto',
                flex: 1,
                ...TABLE_SCROLLBAR_SX,
                '& .MuiTableCell-root': {
                  padding: '4px 8px',
                  fontSize: '13px',
                },
              }}
            >
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell
                      align="center"
                      sx={{
                        width: '20px',
                        ...headerStyle,
                        cursor: 'default',
                      }}
                    />
                    <TableCell
                      align="center"
                      sx={{
                        width: '50px',
                        ...headerStyle,
                        cursor: 'default',
                      }}
                    >
                      {/* Select All Checkbox - only for visible/paginated cards */}
                      <Checkbox
                        size="small"
                        checked={paginatedCards.length > 0 && paginatedCards.every(card => selectedCards.has(card.accountCardId))}
                        indeterminate={paginatedCards.some(card => selectedCards.has(card.accountCardId)) && !paginatedCards.every(card => selectedCards.has(card.accountCardId))}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          paginatedCards.forEach(card => {
                            handleCardSelect(card.accountCardId, checked);
                          });
                        }}
                        sx={{
                          color: '#304FFF',
                          '&.Mui-checked': {
                            color: '#304FFF',
                          },
                        }}
                      />
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        width: '140px',
                        ...headerStyle,
                        cursor: 'default',
                      }}
                    >
                      <Tooltip title={t('ClickToViewTransactions', { defaultValue: 'Click row to view all transactions' })}>
                        <Button
                          size="small"
                          startIcon={<Visibility sx={{ fontSize: '16px', fontWeight: 'bold' }} />}
                          sx={{
                            fontSize: '13px',
                            fontWeight: '700',
                            padding: '4px 8px',
                            minWidth: 'auto',
                            color: '#304FFF',
                            textTransform: 'none',
                            letterSpacing: '0.5px',
                            '&:hover': {
                              backgroundColor: 'rgba(48, 79, 255, 0.15)',
                            },
                          }}
                          disabled
                        >
                          {t('ViewTransactions', { defaultValue: 'View Transactions' })}
                        </Button>
                      </Tooltip>
                    </TableCell>
                    {parentCols.map((c) => (
                      <TableCell
                        key={c.id}
                        align="center"
                        sx={{
                          width: c.width,
                          ...headerStyle,
                        }}
                        onClick={() => c.type && handleHeaderSort(c.id, c.type)}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {c.label}
                          {getSortIcon(c.id)}
                        </Box>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {paginatedCards.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={parentCols.length + 3} align="center">
                        {t('NoData')}
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedCards.map((card) => {
                      const isSelected = selectedCardId === card.accountCardId;
                      const hasTransactions = card.transactions && card.transactions.length > 0;
                      const hasInitialBalance = (card.initialDebit || 0) !== 0 || (card.initialCredit || 0) !== 0;
                      const isClickable = hasTransactions || hasInitialBalance;

                      return (
                        <TableRow
                          key={card.accountCardId}
                          onClick={(e) => isClickable && handleCardClick(card.accountCardId, e)}
                          sx={{
                            backgroundColor: isSelected
                              ? 'rgba(48, 79, 255, 0.15)'
                              : 'rgba(234, 237, 255, 0.32)',
                            '&:hover': {
                              backgroundColor: isClickable
                                ? 'rgba(191, 200, 255, 0.4)'
                                : 'rgba(191, 200, 255, 0.2)',
                            },
                            cursor: isClickable ? 'pointer' : 'default',
                            borderTop: '1px solid rgba(191, 191, 193, 0.56)',
                            transition: 'background-color 0.2s ease',
                            height: '36px',
                          }}
                        >
                          {/* Transaction Indicator Icon */}
                          <TableCell align="center" sx={{ width: '20px', padding: '2px 4px' }}>
                            {(hasTransactions || hasInitialBalance) && (
                              <Tooltip title={hasTransactions ? t('HasTransactions') : t('HasOpeningBalance', { defaultValue: 'Has Opening Balance' })}>
                                <Receipt
                                  sx={{
                                    fontSize: '14px',
                                    color: '#304FFF',
                                    opacity: 0.7,
                                  }}
                                />
                              </Tooltip>
                            )}
                          </TableCell>

                          {/* Selection Checkbox */}
                          <TableCell align="center" sx={{ width: '50px', padding: '4px' }}>
                            <Checkbox
                              className="card-checkbox"
                              size="small"
                              checked={selectedCards.has(card.accountCardId)}
                              onChange={(e) => {
                                e.stopPropagation();
                                handleCardSelect(card.accountCardId, e.target.checked);
                              }}
                              sx={{
                                color: '#304FFF',
                                '&.Mui-checked': {
                                  color: '#304FFF',
                                },
                              }}
                            />
                          </TableCell>

                          {/* View Transactions Indicator */}
                          <TableCell align="center" sx={{ width: '140px', padding: '6px 4px' }}>
                            {isClickable && (
                              <Tooltip title={t('ClickToViewTransactions', { defaultValue: 'Click row to view all transactions' })}>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 0.75,
                                    color: '#304FFF',
                                    minHeight: 28,
                                    padding: '5px 8px',
                                    borderRadius: '6px',
                                    backgroundColor: 'rgba(48, 79, 255, 0.08)',
                                    boxSizing: 'border-box',
                                    '&:hover': {
                                      backgroundColor: 'rgba(48, 79, 255, 0.15)',
                                    },
                                  }}
                                >
                                  <Visibility sx={{ fontSize: '15px', fontWeight: 'bold' }} />
                                  <Typography 
                                    variant="caption" 
                                    sx={{ 
                                      fontSize: 'var(--xtram-text-xs, 11px)', 
                                      fontWeight: 700,
                                      letterSpacing: '0.2px',
                                      lineHeight: 1.2,
                                    }}
                                  >
                                    {t('ViewTransactions', { defaultValue: 'View Transactions' })}
                                  </Typography>
                                </Box>
                              </Tooltip>
                            )}
                          </TableCell>

                          {/* PAL Code */}
                          <TableCell align="center">
                            {card.palCode || '-'}
                          </TableCell>

                          {/* Card ID */}
                          <TableCell align="center" sx={{ fontWeight: 500 }}>
                            {card.accountCardId || '-'}
                          </TableCell>

                          {/* Card Name */}
                          <TableCell align="center" sx={{ fontWeight: 500 }}>
                            {card.accountCardName || '-'}
                          </TableCell>

                          {/* Initial Debit */}
                          <TableCell align="center">
                            {beautifyNumber(card.initialDebit || 0)}
                          </TableCell>

                          {/* Initial Credit */}
                          <TableCell align="center">
                            {beautifyNumber(card.initialCredit || 0)}
                          </TableCell>

                          {/* Total Debit */}
                          <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                            {beautifyNumber(card.totalDebit || 0)}
                          </TableCell>

                          {/* Total Credit */}
                          <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                            {beautifyNumber(card.totalCredit || 0)}
                          </TableCell>

                          {/* Balance */}
                          <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                            {beautifyNumber(card.balance || 0)}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}

                  {/* Totals Row */}
                  {filteredCards.length > 0 && (
                    <TableRow sx={{ backgroundColor: '#f5f7ff', fontWeight: 'bold' }}>
                      <TableCell />
                      <TableCell />
                      <TableCell />
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                        {t('Total')}
                      </TableCell>
                      <TableCell />
                      <TableCell />
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                        {beautifyNumber(totals.initialDebit)}
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                        {beautifyNumber(totals.initialCredit)}
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                        {beautifyNumber(totals.totalDebit)}
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                        {beautifyNumber(totals.totalCredit)}
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                        {beautifyNumber(totals.balance)}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination for Parent Table */}
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
              component="div"
              count={filteredCards.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage={t('Rows per page')}
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} ${t('of')} ${count !== -1 ? count : `${t('more than')} ${to}`}`
              }
              sx={{
                '& .MuiTablePagination-toolbar': {
                  minHeight: '36px',
                  padding: '0 8px',
                },
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                  fontSize: '12px',
                  margin: 0,
                },
                '& .MuiTablePagination-select': {
                  fontSize: '12px',
                },
              }}
            />
          </Box>
        )}

        {/* Transactions Table - fixed height */}
        {selectedCardId && selectedCard && (
          <Box
            sx={{
              flex: isTransactionsFullWidth ? '1 1 auto' : '0 0 60%',
              minWidth: isTransactionsFullWidth ? 0 : undefined,
              display: 'flex',
              flexDirection: 'column',
              height: '60vh',
              overflow: 'hidden',
            }}
          >
            {/* Transactions Table Header */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: 'rgba(48, 79, 255, 0.12)',
                borderRadius: '4px 4px 0 0',
                border: '1px solid rgba(48, 79, 255, 0.2)',
                borderBottom: 'none',
              }}
            >
              {/* Action Buttons Row */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  gap: 0.75,
                  p: 0.5,
                  borderBottom: '1px solid rgba(48, 79, 255, 0.2)',
                }}
              >
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<Description sx={{ fontSize: 'var(--xtram-text-sm, 14px) !important' }} />}
                  onClick={() => handleGenerateLedgerCardClick(selectedCard)}
                  sx={{
                    fontSize: 'var(--xtram-text-sm, 13px)',
                    fontWeight: 600,
                    lineHeight: 1.2,
                    minHeight: 'var(--xtram-control-height-sm, 32px)',
                    height: 'var(--xtram-control-height-sm, 32px)',
                    padding: '0 10px',
                    minWidth: 'auto',
                    color: '#FFFFFF',
                    backgroundColor: '#304FFF',
                    letterSpacing: '0.2px',
                    textTransform: 'none',
                    boxShadow: '0 1px 3px rgba(48, 79, 255, 0.25)',
                    '& .MuiButton-startIcon': {
                      marginInlineEnd: '6px',
                      marginInlineStart: 0,
                      '& > *:nth-of-type(1)': {
                        fontSize: 'var(--xtram-text-sm, 14px)',
                      },
                    },
                    '&:hover': {
                      backgroundColor: '#1e3a8a',
                      boxShadow: '0 2px 6px rgba(48, 79, 255, 0.35)',
                    },
                    transition: 'background-color 0.15s ease, box-shadow 0.15s ease',
                  }}
                >
                  {t('GenerateLedgerCard', { defaultValue: 'GenerateLedgerCard' })}
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={
                    <img
                      src={cardMatchingIcon}
                      alt=""
                      style={{
                        width: 'var(--xtram-text-sm, 14px)',
                        height: 'var(--xtram-text-sm, 14px)',
                        filter: 'brightness(0) invert(1)',
                      }}
                    />
                  }
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (selectedCard && onCardMatchingClick) {
                      console.log('onCardMatchingClick(selectedCard)', onCardMatchingClick(selectedCard));
                      onCardMatchingClick(selectedCard);
                    }
                  }}
                  sx={{
                    fontSize: 'var(--xtram-text-sm, 13px)',
                    fontWeight: 600,
                    lineHeight: 1.2,
                    minHeight: 'var(--xtram-control-height-sm, 32px)',
                    height: 'var(--xtram-control-height-sm, 32px)',
                    padding: '0 10px',
                    minWidth: 'auto',
                    color: '#FFFFFF',
                    backgroundColor: '#F9AA2A',
                    letterSpacing: '0.2px',
                    textTransform: 'none',
                    boxShadow: '0 1px 3px rgba(249, 170, 42, 0.25)',
                    '& .MuiButton-startIcon': {
                      marginInlineEnd: '6px',
                      marginInlineStart: 0,
                    },
                    '&:hover': {
                      backgroundColor: '#d68910',
                      boxShadow: '0 2px 6px rgba(249, 170, 42, 0.35)',
                    },
                    transition: 'background-color 0.15s ease, box-shadow 0.15s ease',
                  }}
                >
                  {t('CardMatching', { defaultValue: 'Card Matching' })}
                </Button>
              </Box>
              
              {/* Header Row with Collapse/Expand and Close */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  p: 1,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                  <Tooltip title={isTransactionsExpanded ? t('Collapse', { defaultValue: 'Collapse' }) : t('Expand', { defaultValue: 'Expand' })}>
                    <IconButton
                      size="small"
                      onClick={() => setIsTransactionsExpanded(!isTransactionsExpanded)}
                      sx={{ color: '#304FFF', padding: '4px' }}
                    >
                      {isTransactionsExpanded ? (
                        <KeyboardArrowUp fontSize="small" />
                      ) : (
                        <KeyboardArrowDown fontSize="small" />
                      )}
                    </IconButton>
                  </Tooltip>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '13px',
                      fontWeight: 600,
                      color: '#304FFF',
                    }}
                  >
                    {t('Transactions')} - {selectedCard.accountCardName} ({filteredTransactions.length > 0 ? filteredTransactions.length : (firstRowForCard ? t('OpeningBalance', { defaultValue: 'Opening Balance' }) : '0')})
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Tooltip title={isTransactionsFullWidth ? t('ShowBothTables') : t('ShowOnlyTransactions')}>
                    <IconButton
                      size="small"
                      onClick={() => setIsTransactionsFullWidth(!isTransactionsFullWidth)}
                      sx={{ color: '#304FFF', padding: '4px' }}
                    >
                      {isTransactionsFullWidth ? (
                        <CloseFullscreen fontSize="small" />
                      ) : (
                        <OpenInFull fontSize="small" />
                      )}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t('CloseTransactions', { defaultValue: 'Close Transactions' })}>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSelectedCardId(null);
                        setIsTransactionsFullWidth(false);
                      }}
                      sx={{ color: 'red', padding: '4px' }}
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </Box>

            {/* Wrapper so Collapse gets a real height (remaining space under header); then table + pagination fit inside */}
            <Box
              sx={{
                flex: isTransactionsExpanded ? 1 : 0,
                minHeight: 0,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Collapse
                in={isTransactionsExpanded}
                timeout="auto"
                sx={{
                  flex: 1,
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  '& .MuiCollapse-wrapper': { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' },
                  '& .MuiCollapse-wrapperInner': { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' },
                }}
              >
                <div
                  className={direction}
                  style={{
                    width: '100%',
                    height: '100%',
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    border: '1px solid rgba(191, 200, 255, 0.3)',
                    borderRadius: '0 0 4px 4px',
                    boxSizing: 'border-box',
                    overflow: 'hidden',
                  }}
                >
                {/* Search row + Filters */}
                <Box
                  dir={direction}
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 1,
                    p: 1,
                    flex: '0 0 auto',
                    width: '100%',
                    minWidth: 0,
                    boxSizing: 'border-box',
                  }}
                >
                  <TextField
                    size="small"
                    dir={direction}
                    placeholder={t('Search')}
                    value={childSearchQuery}
                    onChange={(e) => setChildSearchQuery(e.target.value)}
                    className="document-search-bar"
                    variant="standard"
                    InputProps={{
                      disableUnderline: true,
                      className: `custom-input-box-sizing ${direction === 'ltr' ? 'ltr-input' : 'rtl-input'}`,
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon className="document-search-icon" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Button
                    size="small"
                    type="button"
                    variant="outlined"
                    startIcon={<FilterList sx={{ fontSize: 'var(--xtram-text-sm, 14px) !important' }} />}
                    onClick={() => setShowColumnFilters((prev) => !prev)}
                    sx={{
                      fontFamily: 'var(--xtram-font, Montserrat, sans-serif)',
                      fontSize: 'var(--xtram-text-xs, 12px)',
                      fontWeight: 600,
                      textTransform: 'none',
                      minWidth: 'auto',
                      minHeight: 'var(--xtram-control-height-sm, 32px)',
                      height: 'var(--xtram-control-height-sm, 32px)',
                      px: 1.25,
                      borderRadius: '10px',
                      border: '2px solid',
                      borderColor: showColumnFilters ? '#304FFF' : '#e5e7f6',
                      backgroundColor: showColumnFilters ? 'rgba(48, 79, 255, 0.1)' : '#f5f6fd',
                      color: '#304FFF',
                      boxShadow: showColumnFilters ? '0 0 0 2px rgba(48, 79, 255, 0.08)' : 'none',
                      '& .MuiButton-startIcon': {
                        marginInlineEnd: '6px',
                        marginInlineStart: 0,
                      },
                      '&:hover': {
                        borderColor: '#304FFF',
                        backgroundColor: showColumnFilters ? 'rgba(48, 79, 255, 0.14)' : 'rgba(48, 79, 255, 0.08)',
                      },
                    }}
                  >
                    {t('Filters', { defaultValue: 'Filters' })}
                    {activeFilterCount > 0 && (
                      <Box
                        component="span"
                        sx={{
                          ml: 0.75,
                          px: 0.75,
                          minWidth: 18,
                          height: 18,
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '999px',
                          bgcolor: '#304FFF',
                          color: '#fff',
                          fontSize: 'var(--xtram-text-xs, 11px)',
                          fontWeight: 700,
                          lineHeight: 1,
                        }}
                      >
                        {activeFilterCount}
                      </Box>
                    )}
                  </Button>
                  {activeFilterCount > 0 && (
                    <Button
                      size="small"
                      type="button"
                      onClick={clearColumnFilters}
                      sx={{
                        fontFamily: 'var(--xtram-font, Montserrat, sans-serif)',
                        fontSize: 'var(--xtram-text-xs, 12px)',
                        fontWeight: 600,
                        textTransform: 'none',
                        minHeight: 'var(--xtram-control-height-sm, 32px)',
                        height: 'var(--xtram-control-height-sm, 32px)',
                        px: 1,
                        color: '#304FFF',
                        '&:hover': { backgroundColor: 'rgba(48, 79, 255, 0.08)' },
                      }}
                    >
                      {t('Clear', { defaultValue: 'Clear' })}
                    </Button>
                  )}
                </Box>

                <TableContainer
                  component={Paper}
                  sx={{ flex: 1, minHeight: 0, overflow: 'auto', overflowX: 'auto', ...TABLE_SCROLLBAR_SX }}
                >
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        {childTableCols.map((c) => (
                          <TableCell
                            key={c.id}
                            align="center"
                            sx={{
                              width: c.width,
                              ...childHeaderStyle,
                              cursor: c.type === 'action' ? 'default' : 'pointer',
                            }}
                            onClick={() => {
                              if (c.type === 'action') return;
                              setChildSortModel((prev) => {
                                const field = c.id;
                                const next = prev?.[0]?.field === field && prev?.[0]?.sort === 'asc'
                                  ? [{ field, sort: 'desc' }]
                                  : [{ field, sort: 'asc' }];
                                return next;
                              });
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              {c.label}
                              {getChildSortIcon(c.id)}
                            </Box>
                          </TableCell>
                        ))}
                      </TableRow>
                      {showColumnFilters && (
                        <TableRow sx={{ backgroundColor: 'rgba(245, 245, 246, 0.9)' }}>
                          {childTableCols.map((c) => (
                            <TableCell key={c.id} align="center" sx={{ width: c.width, py: 0.5, px: 0.5 }}>
                              {c.type === 'action' ? null : (
                                <TextField
                                  size="small"
                                  placeholder={t('Filter', { defaultValue: 'Filter' })}
                                  value={columnFilters[c.id] ?? ''}
                                  onChange={(e) => handleColumnFilterChange(c.id, e.target.value)}
                                  onClick={(e) => e.stopPropagation()}
                                  sx={{
                                    '& .MuiInputBase-input': { fontSize: 11 },
                                    '& .MuiInputBase-root': { minHeight: 28 },
                                  }}
                                  inputProps={{ style: { textAlign: 'center' } }}
                                />
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                      )}
                    </TableHead>
                    <TableBody>
                      {/* Opening Balance – first row, always visible when scrolling */}
                      {firstRowForCard && (
                        <TableRow sx={{ backgroundColor: 'rgba(255, 193, 7, 0.1)', borderTop: '2px solid rgba(255, 193, 7, 0.3)' }}>
                          {childTableCols.map((c) => {
                            const isDesc = c.id === 'description';
                            const descText = isDesc
                              ? t(firstRowForCard.description, { defaultValue: firstRowForCard.description })
                              : null;

                            return (
                              <TableCell
                                key={c.id}
                                align="center"
                                sx={{
                                  fontSize: 12,
                                  ...(isDesc && {
                                    maxWidth: 150,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }),
                                }}
                              >
                                {c.id === 'edit'
                                  ? null
                                  : c.id === 'debitAccount'
                                    ? beautifyNumber(firstRowForCard.debitAccountAmount || 0)
                                    : c.id === 'creditAccount'
                                      ? beautifyNumber(firstRowForCard.creditAccountAmount || 0)
                                      : c.id === 'balance'
                                        ? beautifyNumber(firstRowForCard.balance || 0)
                                        : c.id === 'foreignCurrencyDebit'
                                          ? beautifyNumber(firstRowForCard.foreignCurrencyDebit || 0)
                                        : c.id === 'foreignCurrencyCredit'
                                          ? beautifyNumber(firstRowForCard.foreignCurrencyCredit || 0)
                                        : c.id === 'foreignCurrencyBalance'
                                          ? beautifyNumber(firstRowForCard.foreignCurrencyBalance ?? (fcInitialBalance || 0))
                                        : isDesc
                                          ? (
                                            <Tooltip title={descText || ''} arrow>
                                              <span>{descText}</span>
                                            </Tooltip>
                                          )
                                          : '-'}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      )}
                      {/* Data rows – click calls handleEdit */}
                      {paginatedChildRows.length === 0 && !firstRowForCard ? (
                        <TableRow>
                          <TableCell colSpan={childTableCols.length} align="center">
                            {t('NoTransactions')}
                          </TableCell>
                        </TableRow>
                      ) : (
                        displayedChildRows.map((row) => (
                          <TableRow
                            key={row.id}
                            onClick={() => {
                              const target = row._sourceRow || row;
                              setSelectedTransactionId(target.id);
                              if (handleEdit) handleEdit(target);
                            }}
                            sx={{
                              cursor: 'pointer',
                              '&:hover': { backgroundColor: 'rgba(191, 200, 255, 0.25)' },
                              // Rows merged by the Summary view read as one line; tint them so the
                              // merge is visible without reading the chip.
                              ...(row._summary && {
                                backgroundColor: 'rgba(48, 79, 255, 0.07)',
                                borderInlineStart: '3px solid #304FFF',
                              }),
                              ...(row._summaryChild && summaryChildRowSx),
                            }}
                          >
                            {childTableCols.map((c) => {
                              const cellValue = c.id === 'edit' ? null : formatCellValue(row[c.id], c.type, formatDateNew, t);
                              const isDesc = c.id === 'description';

                              return (
                                <TableCell
                                  key={c.id}
                                  align="center"
                                  sx={{
                                    fontSize: 12,
                                    py: 0.5,
                                    ...(isDesc && {
                                      maxWidth: row._summary || row._summaryChild ? 230 : 150,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }),
                                  }}
                                >
                                  {c.id === 'edit' ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
                                      <IconButton size="small" onClick={() => handleEdit(row._sourceRow || row)}>
                                        <img src={editIconImage} alt="edit" />
                                      </IconButton>
                                      <Typography variant="caption" sx={{ fontSize: '10px', color: '#304FFF' }}>
                                        {t('ViewTransaction', { defaultValue: 'View Transaction' })}
                                      </Typography>
                                    </Box>
                                  ) : isDesc && row._summary ? (
                                    <SummaryRowLabel
                                      row={row}
                                      text={cellValue}
                                      t={t}
                                      expanded={!!expandedSummaryRows[row.id]}
                                      onToggle={() => toggleSummaryRow(row.id)}
                                    />
                                  ) : isDesc && row._summaryChild ? (
                                    <Tooltip title={cellValue || ''} arrow>
                                      <span>
                                        <SummaryChildLabel text={cellValue} />
                                      </span>
                                    </Tooltip>
                                  ) : isDesc ? (
                                    <Tooltip title={cellValue || ''} arrow>
                                      <span>{cellValue}</span>
                                    </Tooltip>
                                  ) : (
                                    cellValue
                                  )}
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        ))
                      )}
                      {/* Totals row – always visible when scrolling */}
                      {(filteredTransactions.length > 0 || firstRowForCard) && (
                        <TableRow sx={{ backgroundColor: '#f5f7ff', fontWeight: 'bold' }}>
                          {childTableCols.map((c) => (
                            <TableCell key={c.id} align="center" sx={{ fontWeight: 'bold', fontSize: 12 }}>
                              {c.id === 'edit'
                                ? null
                                : c.id === 'creditAccountName'
                                  ? t('Total')
                                  : c.id === 'debitAccount'
                                    ? beautifyNumber(transactionTotals.debit)
                                    : c.id === 'creditAccount'
                                      ? beautifyNumber(transactionTotals.credit)
                                      : c.id === 'balance'
                                        ? beautifyNumber(transactionTotals.balance)
                                        : c.id === 'foreignCurrencyDebit'
                                          ? beautifyNumber(transactionTotals.fcDebit)
                                        : c.id === 'foreignCurrencyCredit'
                                          ? beautifyNumber(transactionTotals.fcCredit)
                                        : c.id === 'foreignCurrencyBalance'
                                          ? beautifyNumber(transactionTotals.fcBalance)
                                        : null}
                            </TableCell>
                          ))}
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                <TablePagination
                  component="div"
                  count={sortedChildRows.length}
                  page={transactionsPage}
                  onPageChange={(_, p) => setTransactionsPage(p)}
                  rowsPerPage={transactionsRowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setTransactionsRowsPerPage(parseInt(e.target.value, 10));
                    setTransactionsPage(0);
                  }}
                  rowsPerPageOptions={[5, 10, 25, 50, 100]}
                  labelRowsPerPage={t('Rows per page')}
                  labelDisplayedRows={({ from, to, count }) =>
                    `${from}-${to} ${t('of')} ${count !== -1 ? count : `${t('more than')} ${to}`}`
                  }
                  sx={{
                    flexShrink: 0,
                    borderTop: '1px solid rgba(191, 200, 255, 0.3)',
                    backgroundColor: '#fafafa',
                    '& .MuiTablePagination-toolbar': { minHeight: 36 },
                    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: '12px' },
                    '& .MuiTablePagination-select': { fontSize: '12px' },
                  }}
                />
                </div>
              </Collapse>
            </Box>
          </Box>
        )}
      </Box>
    </div>
  );
};

export default AccountCardCollapsibleTable;
